import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { INDIAN_STATES } from '../utils/gst';
import { buildClientAddress } from '../utils/format';
import SearchableSelect from '../components/SearchableSelect';

const DEFAULT_ITEM = { description: '', hsn_sac_code: '998391', quantity: 1, unit_price: '', gst_rate: 18 };

function fmtDisp(n) {
  return parseFloat(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function InvoiceEditPage({ defaultType='invoice' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const isEdit = !!id;
  const isQuotation = defaultType === 'quotation';
  const prefill = !isEdit ? location.state : null; // { client_id, items } — from Work Log "Create Invoice", each item may carry entry_id

  const [bizState, setBizState] = useState('Maharashtra');
  const [form, setForm] = useState({
    type: defaultType,
    client_id: '',
    client_name: '', client_address: '', client_gstin: '', client_pan: '',
    client_state: 'Maharashtra', client_contact: '', client_phone: '', client_email: '',
    po_number: '', vendor_number: '', payment_terms: '7 days', notes: '',
    valid_until: '', due_at: '',
  });
  const [items, setItems] = useState([{ ...DEFAULT_ITEM }]);
  // When on, Unit Price inputs are read as the final GST-inclusive price and the pre-tax base
  // is back-calculated — purely a data-entry convenience, never sent to the backend as-is.
  const [gstInclusive, setGstInclusive] = useState(false);
  const [discount, setDiscount] = useState('');
  // Reference image (from the Work Log entry) being enlarged — a working aid only, never saved to the invoice.
  const [refImg, setRefImg] = useState(null);
  useEffect(() => {
    if (!refImg) return;
    const h = e => { if (e.key === 'Escape') setRefImg(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [refImg]);
  const [targetTotal, setTargetTotal] = useState('');

  // Load business state for GST determination
  useEffect(() => {
    api.get('/clients/business').then(r => { if (r.data?.state) setBizState(r.data.state); }).catch(() => {});
  }, []);

  // Load clients for selector
  const { data: clients=[] } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });

  // Pre-fill from Work Log "Create Invoice" (selected entries), once clients are loaded so client details can auto-fill
  useEffect(() => {
    if (!prefill?.items?.length || !clients.length) return;
    handleClientChange(prefill.client_id);
    setItems(prefill.items);
    toast.success(`${prefill.items.length} entr${prefill.items.length===1?'y':'ies'} loaded from Work Log`);
  }, [clients.length]);

  // Next invoice number preview (new invoices only)
  const { data: nextNumData } = useQuery({
    queryKey: ['invoice-next-number', defaultType],
    queryFn: () => api.get(`/invoices/next-number?type=${defaultType}`).then(r => r.data),
    enabled: !isEdit,
    staleTime: 0,
  });

  // Load existing invoice if editing
  const { data: existing } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get(`/invoices/${id}`).then(r => r.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    // If saved address is empty but we know the client, auto-fill from client data
    const client = clients.find(c => c.id === existing.client_id);
    const addr = existing.client_address || (client ? buildClientAddress(client) : '');
    setForm({
      type: existing.type,
      client_id: existing.client_id || '',
      client_name: existing.client_name || '',
      client_address: addr,
      client_gstin: existing.client_gstin || '',
      client_pan: existing.client_pan || '',
      client_state: existing.client_state || 'Maharashtra',
      client_contact: existing.client_contact || '',
      client_phone: existing.client_phone || '',
      client_email: existing.client_email || '',
      po_number: existing.po_number || '',
        vendor_number: existing.vendor_number || '',
      payment_terms: existing.payment_terms || '7 days',
      notes: existing.notes || '',
      valid_until: existing.valid_until ? existing.valid_until.slice(0, 10) : '',
      due_at: existing.due_at ? existing.due_at.slice(0, 10) : '',
    });
    if (parseFloat(existing.discount_amount) > 0) setDiscount(String(parseFloat(existing.discount_amount)));
    if (existing.items?.length) {
      setItems(existing.items.map(it => ({
        description: it.description,
        hsn_sac_code: it.hsn_sac_code,
        quantity: it.quantity,
        unit_price: it.unit_price,
        gst_rate: it.gst_rate,
        entry_id: it.entry_id || null,
        proof_image: it.proof_image || null,
      })));
    }
  }, [existing, clients]);

  // Auto-fill client details when client selected
  function handleClientChange(clientId) {
    const client = clients.find(c => c.id === clientId);
    if (!client) { setForm(f => ({ ...f, client_id: '', client_name: '', client_address: '', client_gstin: '', client_pan: '', client_state: 'Maharashtra', client_contact: '', client_phone: '', client_email: '' })); return; }
    setForm(f => ({
      ...f,
      client_id: clientId,
      client_name: client.name,
      client_address: buildClientAddress(client),
      client_gstin: client.gstin || '',
      client_pan: client.pan || '',
      client_state: client.state || 'Maharashtra',
      client_contact: client.contact_person || '',
      client_phone: client.phone || '',
      client_email: client.email || '',
    }));
  }

  // Calculate totals
  const isSameState = bizState === form.client_state;
  let subtotal = 0;
  const calcItems = items.map(item => {
    const qty = parseFloat(item.quantity)||0;
    const rate = parseFloat(item.gst_rate)||0;
    const typedPrice = parseFloat(item.unit_price)||0;
    let amt, gstAmt;
    if (gstInclusive) {
      // Typed price is the final, GST-inclusive per-unit price — back out the pre-tax base.
      const inclusiveTotal = typedPrice * qty;
      amt = rate > 0 ? inclusiveTotal / (1 + rate/100) : inclusiveTotal;
      gstAmt = inclusiveTotal - amt;
    } else {
      amt = typedPrice * qty;
      gstAmt = amt * rate / 100;
    }
    subtotal += amt;
    return { ...item, amount: amt, gst_amount: gstAmt };
  });
  const totalGstGross = calcItems.reduce((s, i) => s + i.gst_amount, 0);
  // Discount reduces the taxable value; GST is scaled down proportionally so mixed GST rates
  // across line items stay correct — mirrors exactly what the backend does on save.
  const discountAmt = Math.max(0, Math.min(parseFloat(discount) || 0, subtotal));
  const discountRatio = subtotal > 0 ? discountAmt / subtotal : 0;
  const netTaxable = subtotal - discountAmt;
  const totalGst = totalGstGross * (1 - discountRatio);
  const cgst = isSameState ? totalGst / 2 : 0;
  const sgst = isSameState ? totalGst / 2 : 0;
  const igst = !isSameState ? totalGst : 0;
  const exactTotal = netTaxable + totalGst;
  const grandTotal = Math.round(exactTotal);
  const roundedOff = grandTotal - exactTotal;

  // "Round to a target total" helper — back-calculates the discount needed to land on a
  // client-requested round figure (e.g. "8732 → make it 8000"), using the current blended GST rate.
  function applyTargetTotal() {
    const target = parseFloat(targetTotal);
    if (!target || target <= 0) { toast.error('Enter a target amount first'); return; }
    if (subtotal <= 0) { toast.error('Add line items first'); return; }
    const blendedRate = totalGstGross / subtotal;
    const requiredNetTaxable = target / (1 + blendedRate);
    const requiredDiscount = Math.max(0, subtotal - requiredNetTaxable);
    setDiscount(requiredDiscount.toFixed(2));
    setTargetTotal('');
  }

  const saveMutation = useMutation({
    mutationFn: data => isEdit
      ? api.patch(`/invoices/${id}`, data)
      : api.post('/invoices', data),
    onSuccess: res => {
      toast.success(isEdit ? 'Updated' : `${isQuotation ? 'Quotation' : 'Invoice'} ${res.data.invoice_number} created`);
      qc.invalidateQueries(['invoices']);
      navigate(isQuotation ? '/quotations' : '/invoices');
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed to save'),
  });

  function handleSave() {
    if (!form.client_name) { toast.error('Enter client name'); return; }
    const validItems = items.filter(i => i.description && i.unit_price);
    if (!validItems.length) { toast.error('Add at least one line item with description and price'); return; }
    // Backend always expects a pre-tax unit_price — convert here so it never needs to know
    // this "inclusive" mode exists.
    const itemsToSend = gstInclusive
      ? validItems.map(it => {
          const rate = parseFloat(it.gst_rate)||0;
          const typedPrice = parseFloat(it.unit_price)||0;
          return { ...it, unit_price: rate > 0 ? typedPrice / (1 + rate/100) : typedPrice };
        })
      : validItems;
    // Only entries whose line item actually survived to Save get marked "Billed" — an item
    // deleted from this draft (e.g. client asked to hold half the work for next month) must NOT
    // lock its Work Log entry to this invoice.
    const entryIds = validItems.filter(i => i.entry_id).map(i => i.entry_id);
    const cleanItems = itemsToSend.map(({ proof_image, ...rest }) => rest);
    saveMutation.mutate({ ...form, items: cleanItems, discount_amount: discountAmt, ...(entryIds.length && { entry_ids: entryIds }) });
  }

  function addItem() { setItems(p => [...p, { ...DEFAULT_ITEM }]); }
  function removeItem(idx) { setItems(p => p.filter((_, i) => i !== idx)); }
  function updateItem(idx, field, val) { setItems(p => p.map((it, i) => i === idx ? { ...it, [field]: val } : it)); }

  const title = isEdit ? `Edit ${isQuotation ? 'Quotation' : 'Invoice'}` : `New ${isQuotation ? 'Quotation' : 'Invoice'}`;

  return (
    <div>
      <div className="section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(isQuotation ? '/quotations' : '/invoices')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-secondary)', padding: '0 4px', lineHeight: 1 }} title="Back to list">←</button>
          <h2>{title}</h2>
          {!isEdit && nextNumData?.number && (
            <span style={{ fontSize: 12, fontWeight: 600, color: '#073E81', background: '#E8EEF8', padding: '3px 10px', borderRadius: 20, border: '1px solid #b8cce8' }}>
              #{nextNumData.number}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate(isQuotation ? '/quotations' : '/invoices')}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : `Create ${isQuotation ? 'Quotation' : 'Invoice'}`}
          </button>
        </div>
      </div>

      <div className="form-grid" style={{ marginBottom: 16, alignItems: 'start' }}>
        {/* Left: Client details */}
        <div className="card">
          <div className="card-title">Client details</div>
          <div className="field">
            <label>Select existing client (optional)</label>
            <SearchableSelect value={form.client_id} onChange={handleClientChange} items={clients}
              allowClear clearLabel="— Or type manually below —" placeholder="— Or type manually below —" />
          </div>
          <div className="field"><label>Client / Party name *</label>
            <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Reshma Mangesh Mane"/>
          </div>
          <div className="field"><label>Address</label>
            <textarea rows={3} value={form.client_address} onChange={e => setForm(f => ({ ...f, client_address: e.target.value }))} placeholder="Full address with city, pin"/>
          </div>
          <div className="form-grid">
            <div className="field"><label>GSTIN</label>
              <input value={form.client_gstin} onChange={e => setForm(f => ({ ...f, client_gstin: e.target.value }))} placeholder="27XXXXX"/>
            </div>
            <div className="field"><label>PAN</label>
              <input value={form.client_pan} onChange={e => setForm(f => ({ ...f, client_pan: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F" maxLength={10}/>
            </div>
          </div>
          <div className="form-grid">
            <div className="field"><label>State</label>
              <select value={form.client_state} onChange={e => setForm(f => ({ ...f, client_state: e.target.value }))}>
                {INDIAN_STATES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-grid">
            <div className="field"><label>Contact person</label>
              <input value={form.client_contact} onChange={e => setForm(f => ({ ...f, client_contact: e.target.value }))} placeholder="Mangesh"/>
            </div>
            <div className="field"><label>Phone</label>
              <input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} placeholder="9860647088"/>
            </div>
          </div>
          <div className="field"><label>Email</label>
            <input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} placeholder="client@email.com"/>
          </div>
          {/* GST type indicator */}
          <div style={{ fontSize: 11, color: 'var(--text2)', padding: '6px 8px', background: 'var(--surface2)', borderRadius: 4, marginTop: 4 }}>
            {isSameState
              ? `✓ Same state (${bizState}) → CGST + SGST will apply`
              : `→ Different state → IGST will apply`}
          </div>
        </div>

        {/* Right: Invoice meta */}
        <div className="card">
          <div className="card-title">Invoice details</div>
          <div className="form-grid">
            <div className="field"><label>PO Number</label>
              <input value={form.po_number} onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))} placeholder="Optional"/>
            </div>
            <div className="field"><label>Vendor Number</label>
              <input value={form.vendor_number} onChange={e => setForm(f => ({ ...f, vendor_number: e.target.value }))} placeholder="Optional"/>
            </div>
          </div>
          <div className="form-grid">
            {isQuotation
              ? <div className="field"><label>Valid until</label>
                  <input type="date" value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}/>
                </div>
              : <div className="field"><label>Payment terms</label>
                  <input value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} placeholder="7 days"/>
                </div>
            }
          </div>
          {!isQuotation && (
            <div className="field"><label>Due date (optional)</label>
              <input type="date" value={form.due_at} onChange={e => setForm(f => ({ ...f, due_at: e.target.value }))}/>
            </div>
          )}
          <div className="field"><label>Notes (printed on invoice)</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Course — Grassroots Advocacy Campaign Development for Nonprofits"/>
          </div>

          {/* Discount */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
            <div className="card-title" style={{ marginBottom: 8 }}>Discount</div>
            <div className="form-grid">
              <div className="field"><label>Discount (₹)</label>
                <input type="number" min="0" step="0.01" value={discount} onChange={e => setDiscount(e.target.value)} placeholder="0.00"/>
              </div>
              <div className="field"><label>Or round final total to (₹)</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="number" min="0" step="1" value={targetTotal} onChange={e => setTargetTotal(e.target.value)} placeholder="e.g. 8000" style={{ flex: 1 }}/>
                  <button type="button" className="btn-sm" onClick={applyTargetTotal}>Apply</button>
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>
              Discount reduces the taxable amount and GST recalculates accordingly — the printed invoice will show it as its own line, so the client sees exactly what was taken off.
            </div>
          </div>

          {/* Live totals preview */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
            <div className="card-title" style={{ marginBottom: 8 }}>Totals preview</div>
            <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Taxable amount</span><span>₹{fmtDisp(subtotal)}</span></div>
              {discountAmt > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#C0392B' }}><span>Less: Discount</span><span>−₹{fmtDisp(discountAmt)}</span></div>
              )}
              {isSameState ? <>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text2)' }}><span>CGST</span><span>₹{fmtDisp(cgst)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text2)' }}><span>SGST</span><span>₹{fmtDisp(sgst)}</span></div>
              </> : (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text2)' }}><span>IGST</span><span>₹{fmtDisp(igst)}</span></div>
              )}
              {Math.abs(roundedOff) > 0.001 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text2)' }}><span>Rounded off</span><span>₹{fmtDisp(Math.abs(roundedOff))}</span></div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 4, color: 'var(--accent)' }}>
                <span>Grand Total</span><span>₹{fmtDisp(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Line items</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-sm" title="Type the final price and the pre-tax rate is calculated for you"
              onClick={() => setGstInclusive(v => !v)}
              style={{ background: gstInclusive ? 'var(--accent)' : '', color: gstInclusive ? '#fff' : '', fontWeight: gstInclusive ? 600 : 400 }}>
              {gstInclusive ? '✓ ' : ''}Prices include GST
            </button>
            <button className="btn-sm" onClick={addItem}>+ Add row</button>
          </div>
        </div>
        {gstInclusive && (
          <div style={{ fontSize: 11, color: 'var(--text2)', padding: '6px 8px', background: 'var(--surface2)', borderRadius: 4, marginBottom: 10 }}>
            Unit Price below is the final, GST-inclusive price — the pre-tax rate is calculated automatically for the invoice.
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 260, textAlign: 'left', padding: '6px 8px' }}>Description *</th>
                <th style={{ width: 90, textAlign: 'center', padding: '6px 8px' }}>HSN/SAC</th>
                <th style={{ width: 70, textAlign: 'center', padding: '6px 8px' }}>Qty</th>
                <th style={{ width: 110, textAlign: 'right', padding: '6px 8px' }}>{gstInclusive ? 'Unit Price incl. GST (₹) *' : 'Unit Price (₹) *'}</th>
                <th style={{ width: 80, textAlign: 'center', padding: '6px 8px' }}>GST %</th>
                <th style={{ width: 90, textAlign: 'right', padding: '6px 8px' }}>{gstInclusive ? 'Amount (pre-tax)' : 'Amount'}</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const calc = calcItems[idx];
                return (
                  <tr key={idx}>
                    <td style={{ padding: '4px 6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {item.proof_image && (
                          <img src={`/uploads/proofs/${item.proof_image}`} alt="Reference" title="Reference image from Work Log — click to enlarge"
                            onClick={() => setRefImg(`/uploads/proofs/${item.proof_image}`)}
                            style={{ height: 44, width: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', cursor: 'zoom-in', flexShrink: 0 }}/>
                        )}
                        <input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                          placeholder="Description of service" style={{ width: '100%', fontSize: 12 }}/>
                      </div>
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input value={item.hsn_sac_code} onChange={e => updateItem(idx, 'hsn_sac_code', e.target.value)}
                        style={{ width: '100%', fontSize: 12, textAlign: 'center' }}/>
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input type="number" min="1" step="1" value={item.quantity}
                        onChange={e => updateItem(idx, 'quantity', e.target.value.replace(/[^\d]/g, ''))}
                        style={{ width: '100%', fontSize: 12, textAlign: 'center' }}/>
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input type="number" min="0" step="0.01" value={item.unit_price}
                        onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                        placeholder="0.00" style={{ width: '100%', fontSize: 12, textAlign: 'right' }}/>
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input type="number" min="0" max="28" value={item.gst_rate}
                        onChange={e => updateItem(idx, 'gst_rate', e.target.value)}
                        style={{ width: '100%', fontSize: 12, textAlign: 'center' }}/>
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>
                      ₹{fmtDisp(calc.amount)}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      {items.length > 1 && (
                        <button className="btn-danger btn-sm" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => removeItem(idx)}>✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={() => navigate(isQuotation ? '/quotations' : '/invoices')}>Cancel</button>
        <button className="btn-primary" onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : `Create ${isQuotation ? 'Quotation' : 'Invoice'}`}
        </button>
      </div>
      {refImg && (
        <div onClick={() => setRefImg(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 24 }}>
          <img src={refImg} alt="Reference" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6, background: '#fff' }}/>
        </div>
      )}
    </div>
  );
}
